import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { arrayOf, bool, func, number, object, oneOf, shape, string } from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import api from '../../api';
import { unitDivisor } from '../../util/currency';
import { useIntl } from '../../util/reactIntl';
import { LOCALE_TO_LANG } from '../../constants/locales';
import ListingService from '../../services/ListingService';
import { getCrionAICopy, pickRandomCrionAIPrompts } from '../CrionAIShowcase/crionAICopy';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import { findOptionsForSelectFilter } from '../../util/search';
import { createURLPaths } from '../../constants/listingAttributes';
import css from './LandingAIChatWidget.module.css';

const ENABLED = process.env.REACT_APP_AI_CHAT_ENABLED === 'true';
const MAX_HISTORY_ITEMS = 6;
const FREE_QUESTIONS_LIMIT = 1;
const AI_CHAT_HANDOFF_STORAGE_KEY = 'crion-ai-chat-handoff';
const AI_CHAT_SIGNUP_GATE_KEY = 'crion-ai-chat-signup-gate';
let messageIdCounter = 0;
const TEXT_BY_LOCALE = {
  'en-US': {
    trigger: 'Ask Crion AI',
    triggerEyebrow: 'Live AI planner',
    triggerSubline: 'Plan outdoor experiences faster',
    title: 'Crion AI Outdoor Assistant',
    placeholder: 'Ask about outdoor experiences...',
    send: 'Send',
    close: 'Close',
    greeting: 'Hi! I can help you find and plan outdoor experiences on Crion.',
    loading: 'Thinking...',
    fallback: 'I could not answer right now. Please try again in a moment.',
    openListing: 'Open listing',
    applyFilters: 'Apply filters',
    nextSession: 'Next session',
    priceFrom: 'From',
    price: 'Price',
    seats: 'Seats left',
    location: 'Location',
    languages: 'Languages',
    group: 'Group',
    level: 'Level',
    duration: 'Duration',
    maxPeople: 'Max people',
    day: 'day',
    days: 'days',
    newChat: 'New chat',
    continueInAiPage: 'Open in Crion AI',
    picksTitle: 'Chat picks',
    picksEmpty: 'Suggested listings will appear here as the chat progresses.',
    previous: 'Previous',
    next: 'Next',
    resultsCount: 'results',
    selectorLabel: 'Suggested listings',
    noImage: 'Crion AI',
    whyChosen: 'Why Crion AI picked this',
    manualRequestPrompt: 'Not finding the right fit yet?',
    manualRequestDescription:
      'You can send your request to the Crion team directly from this chat. We will receive your recent context and help manually.',
    manualRequestButton: 'Send manual request',
    manualRequestFallbackLink: 'Open request form instead',
    manualRequestTitle: 'Send a tailored request',
    manualRequestFirstName: 'First name',
    manualRequestLastName: 'Last name',
    manualRequestEmail: 'Email',
    manualRequestNotes: 'Anything else we should know?',
    manualRequestNotesPlaceholder: 'Destination, dates, group, budget, or what was missing on Crion.',
    manualRequestSubmit: 'Send to Crion',
    manualRequestCancel: 'Cancel',
    manualRequestSuccess: 'Your request was sent to Crion. We will review it manually.',
    manualRequestError: 'Could not send your request right now. Please try again or use the form.',
    signupGateTitle: 'Create a free account to keep planning',
    signupGateDescription: 'Sign up to continue chatting with Crion AI, save your picks, and book outdoor experiences directly.',
    signupGateButton: 'Sign up free',
    signupGateLogin: 'Already have an account? Log in',
  },
  'it-IT': {
    trigger: 'Chiedi a Crion AI',
    triggerEyebrow: 'AI planner live',
    triggerSubline: 'Pianifica esperienze outdoor più velocemente',
    title: 'Assistente Outdoor Crion AI',
    placeholder: 'Chiedi sulle esperienze outdoor...',
    send: 'Invia',
    close: 'Chiudi',
    greeting: 'Ciao! Posso aiutarti a trovare e pianificare esperienze outdoor su Crion.',
    loading: 'Sto pensando...',
    fallback: 'Non riesco a rispondere ora. Riprova tra poco.',
    openListing: 'Apri esperienza',
    applyFilters: 'Applica filtri',
    nextSession: 'Prossima sessione',
    priceFrom: 'Da',
    price: 'Prezzo',
    seats: 'Posti rimasti',
    location: 'Località',
    languages: 'Lingue',
    group: 'Gruppo',
    level: 'Livello',
    duration: 'Durata',
    maxPeople: 'Persone max',
    day: 'giorno',
    days: 'giorni',
    newChat: 'Nuova chat',
    continueInAiPage: 'Apri in Crion AI',
    picksTitle: 'Scelte in chat',
    picksEmpty: 'Le esperienze suggerite appariranno qui mentre la chat avanza.',
    previous: 'Precedente',
    next: 'Successiva',
    resultsCount: 'risultati',
    selectorLabel: 'Esperienze suggerite',
    noImage: 'Crion AI',
    whyChosen: 'Perché Crion AI ha scelto questa',
    manualRequestPrompt: 'Non trovi ancora la soluzione giusta?',
    manualRequestDescription:
      'Puoi inviare la tua richiesta direttamente dal chat a Crion. Riceveremo il contesto recente e ti aiuteremo manualmente.',
    manualRequestButton: 'Invia richiesta manuale',
    manualRequestFallbackLink: 'Apri invece il modulo',
    manualRequestTitle: 'Invia una richiesta su misura',
    manualRequestFirstName: 'Nome',
    manualRequestLastName: 'Cognome',
    manualRequestEmail: 'Email',
    manualRequestNotes: 'Altro da sapere?',
    manualRequestNotesPlaceholder: 'Destinazione, date, gruppo, budget o cosa mancava su Crion.',
    manualRequestSubmit: 'Invia a Crion',
    manualRequestCancel: 'Annulla',
    manualRequestSuccess: 'La tua richiesta è stata inviata a Crion. La valuteremo manualmente.',
    manualRequestError: 'Non è stato possibile inviare la richiesta ora. Riprova o usa il modulo.',
    signupGateTitle: 'Crea un account gratuito per continuare',
    signupGateDescription: 'Registrati per continuare a chattare con Crion AI, salvare le tue scelte e prenotare esperienze outdoor.',
    signupGateButton: 'Registrati gratis',
    signupGateLogin: 'Hai già un account? Accedi',
  },
  'fr-FR': {
    trigger: 'Demander à Crion AI',
    triggerEyebrow: 'Planificateur AI en direct',
    triggerSubline: 'Planifiez plus vite vos expériences outdoor',
    title: 'Assistant Outdoor Crion AI',
    placeholder: 'Posez une question sur les expériences outdoor...',
    send: 'Envoyer',
    close: 'Fermer',
    greeting: 'Bonjour ! Je peux vous aider à trouver et planifier des expériences outdoor sur Crion.',
    loading: 'Je réfléchis...',
    fallback: 'Je ne peux pas répondre pour le moment. Réessayez dans un instant.',
    openListing: 'Ouvrir l\'expérience',
    applyFilters: 'Appliquer les filtres',
    nextSession: 'Prochaine session',
    priceFrom: 'À partir de',
    price: 'Prix',
    seats: 'Places restantes',
    location: 'Lieu',
    languages: 'Langues',
    group: 'Groupe',
    level: 'Niveau',
    duration: 'Durée',
    maxPeople: 'Max personnes',
    day: 'jour',
    days: 'jours',
    newChat: 'Nouveau chat',
    continueInAiPage: 'Ouvrir dans Crion AI',
    picksTitle: 'Sélections du chat',
    picksEmpty: 'Les listings suggérés apparaîtront ici au fil de la conversation.',
    previous: 'Précédent',
    next: 'Suivant',
    resultsCount: 'résultats',
    selectorLabel: 'Listings suggérés',
    noImage: 'Crion AI',
    whyChosen: 'Pourquoi Crion AI a choisi cela',
    manualRequestPrompt: 'Vous ne trouvez pas encore la bonne option ?',
    manualRequestDescription:
      'Vous pouvez envoyer votre demande directement depuis ce chat à l\'équipe Crion. Nous recevrons le contexte récent et vous aiderons manuellement.',
    manualRequestButton: 'Envoyer une demande manuelle',
    manualRequestFallbackLink: 'Ouvrir plutôt le formulaire',
    manualRequestTitle: 'Envoyer une demande sur mesure',
    manualRequestFirstName: 'Prénom',
    manualRequestLastName: 'Nom',
    manualRequestEmail: 'Email',
    manualRequestNotes: 'Autre information utile ?',
    manualRequestNotesPlaceholder: 'Destination, dates, groupe, budget ou ce qui manquait sur Crion.',
    manualRequestSubmit: 'Envoyer à Crion',
    manualRequestCancel: 'Annuler',
    manualRequestSuccess: 'Votre demande a été envoyée à Crion. Nous allons la traiter manuellement.',
    manualRequestError: 'Impossible d\'envoyer votre demande maintenant. Réessayez ou utilisez le formulaire.',
    signupGateTitle: 'Créez un compte gratuit pour continuer',
    signupGateDescription: 'Inscrivez-vous pour continuer à utiliser Crion AI, sauvegarder vos choix et réserver des expériences outdoor.',
    signupGateButton: 'S\'inscrire gratuitement',
    signupGateLogin: 'Déjà un compte ? Se connecter',
  },
  'de-DE': {
    trigger: 'Crion AI fragen',
    triggerEyebrow: 'Live-AI-Planer',
    triggerSubline: 'Outdoor-Erlebnisse schneller planen',
    title: 'Crion AI Outdoor-Assistent',
    placeholder: 'Frage zu Outdoor-Erlebnissen...',
    send: 'Senden',
    close: 'Schließen',
    greeting: 'Hallo! Ich helfe dir, Outdoor-Erlebnisse auf Crion zu finden und zu planen.',
    loading: 'Ich denke nach...',
    fallback: 'Ich kann gerade nicht antworten. Bitte versuche es gleich erneut.',
    openListing: 'Erlebnis öffnen',
    applyFilters: 'Filter anwenden',
    nextSession: 'Nächste Session',
    priceFrom: 'Ab',
    price: 'Preis',
    seats: 'Freie Plätze',
    location: 'Ort',
    languages: 'Sprachen',
    group: 'Gruppe',
    level: 'Niveau',
    duration: 'Dauer',
    maxPeople: 'Max Personen',
    day: 'Tag',
    days: 'Tage',
    newChat: 'Neuer Chat',
    continueInAiPage: 'In Crion AI öffnen',
    picksTitle: 'Chat-Auswahl',
    picksEmpty: 'Hier erscheinen vorgeschlagene Listings im Verlauf des Chats.',
    previous: 'Zurück',
    next: 'Weiter',
    resultsCount: 'Ergebnisse',
    selectorLabel: 'Vorgeschlagene Listings',
    noImage: 'Crion AI',
    whyChosen: 'Warum Crion AI das ausgewählt hat',
    manualRequestPrompt: 'Noch nicht das Richtige gefunden?',
    manualRequestDescription:
      'Du kannst deine Anfrage direkt aus diesem Chat an Crion senden. Wir erhalten den bisherigen Kontext und helfen manuell weiter.',
    manualRequestButton: 'Manuelle Anfrage senden',
    manualRequestFallbackLink: 'Stattdessen Formular öffnen',
    manualRequestTitle: 'Individuelle Anfrage senden',
    manualRequestFirstName: 'Vorname',
    manualRequestLastName: 'Nachname',
    manualRequestEmail: 'E-Mail',
    manualRequestNotes: 'Noch etwas Wichtiges?',
    manualRequestNotesPlaceholder: 'Ziel, Daten, Gruppe, Budget oder was auf Crion gefehlt hat.',
    manualRequestSubmit: 'An Crion senden',
    manualRequestCancel: 'Abbrechen',
    manualRequestSuccess: 'Deine Anfrage wurde an Crion gesendet. Wir prüfen sie manuell.',
    manualRequestError: 'Die Anfrage konnte gerade nicht gesendet werden. Bitte erneut versuchen oder das Formular nutzen.',
    signupGateTitle: 'Erstelle ein kostenloses Konto, um weiterzumachen',
    signupGateDescription: 'Registriere dich, um weiter mit Crion AI zu chatten, deine Auswahl zu speichern und Outdoor-Erlebnisse zu buchen.',
    signupGateButton: 'Kostenlos registrieren',
    signupGateLogin: 'Schon ein Konto? Anmelden',
  },
};

const createMessageId = prefix => {
  messageIdCounter += 1;
  return `${prefix}-${Date.now()}-${messageIdCounter}`;
};

const createChatSessionId = () =>
  `chat-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;

const createMessage = (role, content, extra = {}) => ({
  id: createMessageId(role),
  role,
  content,
  ...extra,
});

const normalizeTransferredMessage = message => {
  const role = message?.role === 'user' ? 'user' : 'assistant';
  const content = typeof message?.content === 'string' ? message.content : '';

  if (!content && !Array.isArray(message?.listings)) {
    return null;
  }

  return {
    id: typeof message?.id === 'string' && message.id ? message.id : createMessageId(role),
    role,
    content,
    listings: Array.isArray(message?.listings) ? message.listings : undefined,
    displayContent: typeof message?.displayContent === 'string' ? message.displayContent : undefined,
    isPlannerPrompt: !!message?.isPlannerPrompt,
    suggestedFilters:
      message?.suggestedFilters && typeof message.suggestedFilters === 'object'
        ? message.suggestedFilters
        : null,
    plannerPreferences:
      message?.plannerPreferences && typeof message.plannerPreferences === 'object'
        ? message.plannerPreferences
        : null,
  };
};

const readSignupGateChatState = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(AI_CHAT_SIGNUP_GATE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(AI_CHAT_SIGNUP_GATE_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// Read a persisted chat session without removing it (so it survives across multiple navigations).
const readPersistedChatSession = key => {
  if (!key || typeof window === 'undefined' || !window.sessionStorage) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const buildInitialChatState = (initialChatState, greeting, gateState) => {
  const source = initialChatState || gateState || null;
  const restoredMessages = Array.isArray(source?.messages)
    ? source.messages.map(normalizeTransferredMessage).filter(Boolean)
    : [];

  return {
    chatSessionId:
      typeof source?.chatSessionId === 'string' && source.chatSessionId
        ? source.chatSessionId
        : createChatSessionId(),
    draft: typeof source?.draft === 'string' ? source.draft : '',
    messages: restoredMessages.length ? restoredMessages : [createMessage('assistant', greeting)],
    activeListingIndexByMessageId:
      source?.activeListingIndexByMessageId &&
      typeof source.activeListingIndexByMessageId === 'object'
        ? source.activeListingIndexByMessageId
        : {},
  };
};

const createChatHandoffState = (messages, draft, activeListingIndexByMessageId, chatSessionId) => ({
  id: `ai-chat-handoff-${Date.now()}`,
  chatSessionId: typeof chatSessionId === 'string' && chatSessionId ? chatSessionId : createChatSessionId(),
  createdAt: Date.now(),
  draft: typeof draft === 'string' ? draft : '',
  messages: Array.isArray(messages)
    ? messages.map(message => ({
        id: message.id,
        role: message.role,
        content: message.content,
        listings: Array.isArray(message.listings) ? message.listings : undefined,
        displayContent: typeof message.displayContent === 'string' ? message.displayContent : undefined,
        isPlannerPrompt: !!message.isPlannerPrompt,
        suggestedFilters: message.suggestedFilters || null,
        plannerPreferences: message.plannerPreferences || null,
      }))
    : [],
  activeListingIndexByMessageId:
    activeListingIndexByMessageId && typeof activeListingIndexByMessageId === 'object'
      ? activeListingIndexByMessageId
      : {},
});

const trimText = (value, maxLength) => {
  if (typeof value !== 'string') {
    return '';
  }

  const normalized = value.trim();
  if (!normalized) {
    return '';
  }

  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
};

const toVisibleChatHistory = (messages, greeting) =>
  (Array.isArray(messages) ? messages : [])
    .filter(message => {
      const content = typeof message?.content === 'string' ? message.content.trim() : '';
      return content && !message?.isPlannerPrompt && content !== greeting;
    })
    .slice(-MAX_HISTORY_ITEMS)
    .map(({ role, content }) => ({ role, content }));

const buildManualRequestMessage = ({
  messages,
  contextPage,
  locale,
  extraNotes,
  latestSuggestedListings,
}) => {
  const recentMessages = Array.isArray(messages) ? messages.slice(-6) : [];
  const transcript = recentMessages
    .map(message => {
      const roleLabel = message?.role === 'user' ? 'Traveler' : 'Crion AI';
      return `${roleLabel}: ${trimText(message?.content || '', 280)}`;
    })
    .filter(Boolean)
    .join('\n');

  const listingsBlock = Array.isArray(latestSuggestedListings) && latestSuggestedListings.length
    ? latestSuggestedListings
        .slice(0, 3)
        .map(listing => {
          const title = trimText(listing?.title || '', 90);
          const support = trimText(listing?.reason || listing?.location || '', 120);
          return `- ${title}${support ? ` | ${support}` : ''}`;
        })
        .join('\n')
    : 'No listing suggestions were shown.';

  const message = [
    'AI chat manual request',
    `Page: ${contextPage || 'unknown'}`,
    `Locale: ${locale || 'unknown'}`,
    '',
    extraNotes ? `User note:\n${trimText(extraNotes, 600)}` : null,
    transcript ? `Recent chat:\n${transcript}` : null,
    `Latest suggested listings:\n${listingsBlock}`,
  ]
    .filter(Boolean)
    .join('\n\n');

  return trimText(message, 2048);
};

const getMessagesForLocale = locale => TEXT_BY_LOCALE[locale] || TEXT_BY_LOCALE['en-US'];
const normalizeLocale = locale => (TEXT_BY_LOCALE[locale] ? locale : 'en-US');

const scheduleFrame = callback => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(callback);
    return;
  }

  setTimeout(callback, 0);
};

const formatSessionDate = (value, locale) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString(normalizeLocale(locale), {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

const toReadableText = value => {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/[_-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
};

const getLocalizedListingTitle = (listing, locale) => {
  if (!listing) return '';
  const lang = LOCALE_TO_LANG[locale] || 'english';
  const msgs = ListingService.generalMessages[lang];
  const discipline = listing.discipline;
  const city = listing.city || listing.activityLocation || '';
  const translatedDiscipline = discipline
    ? (msgs?.[`General.${discipline}`] || toReadableText(discipline))
    : '';
  const inText = msgs?.['General.in'] || 'in';

  if (translatedDiscipline && city) {
    return `${translatedDiscipline} ${inText} ${city}`;
  }
  return listing.title || translatedDiscipline || '';
};

const normalizeSelectFilterValue = value => {
  if (typeof value !== 'string') {
    return '';
  }

  const normalized = value.trim();
  if (!normalized) {
    return '';
  }

  if (normalized.startsWith('has_any:') || normalized.startsWith('has_all:')) {
    return normalized;
  }

  return `has_any:${normalized}`;
};

const normalizePlainFilterValue = value => {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .trim()
    .replace(/^(has_any|has_all):/, '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
    .join(',');
};

const normalizePeopleSearchValue = value => {
  const parsedPeople = Number.parseInt(String(value || '').replace(',', '').trim(), 10);
  return Number.isFinite(parsedPeople) && parsedPeople > 0 ? `${parsedPeople},` : '';
};

const SEARCH_PRICE_MAX_MAJOR_UNIT = 2000;

const normalizePriceSearchAmount = value => {
  const numericValue = Number.parseFloat(String(value || '').trim());
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null;
  }

  const majorUnitValue =
    numericValue > SEARCH_PRICE_MAX_MAJOR_UNIT ? Math.round(numericValue / 100) : numericValue;

  return Math.round(majorUnitValue);
};

const normalizePriceSearchRange = value => {
  const parts = String(value || '')
    .split(',')
    .map(part => normalizePriceSearchAmount(part));

  const hasValidRange =
    parts.length === 2 &&
    parts.every(part => Number.isFinite(part) && part >= 0) &&
    parts[1] >= parts[0] &&
    parts[1] > 0;

  return hasValidRange ? `${parts[0]},${parts[1]}` : '';
};

const normalizeCoordinate = value => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return null;
  }

  return Number(numericValue.toFixed(6));
};

const formatLatLngSearchValue = latLng => {
  const lat = normalizeCoordinate(latLng?.lat);
  const lng = normalizeCoordinate(latLng?.lng);

  return lat != null && lng != null ? `${lat},${lng}` : '';
};

const formatBoundsSearchValue = bounds => {
  const ne = bounds?.ne;
  const sw = bounds?.sw;
  const neValue = formatLatLngSearchValue(ne);
  const swValue = formatLatLngSearchValue(sw);

  return neValue && swValue ? `${neValue},${swValue}` : '';
};

const buildPlannerAddress = location => {
  if (!location || typeof location !== 'object') {
    return '';
  }

  const primaryLocation = location.region || location.city || location.rawText || location.label || '';
  const country = location.country || '';
  const primaryText = String(primaryLocation).trim();
  const countryText = String(country).trim();

  if (!primaryText) {
    return countryText;
  }

  if (!countryText || primaryText.toLowerCase().includes(countryText.toLowerCase())) {
    return primaryText;
  }

  return `${primaryText}, ${countryText}`;
};

const normalizeDateRangeSearchValue = value => {
  const parts = String(value || '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  return parts.length === 2 ? `${parts[0]}, ${parts[1]}` : '';
};

const normalizeSuggestedSearchParams = suggestedFilters => {
  if (!suggestedFilters || typeof suggestedFilters !== 'object') {
    return {};
  }

  const normalized = {};

  if (suggestedFilters.pub_activity) {
    const value = normalizePlainFilterValue(suggestedFilters.pub_activity);
    if (value) {
      normalized.pub_activity = value;
    }
  }

  if (suggestedFilters.pub_discipline) {
    const value = normalizeSelectFilterValue(suggestedFilters.pub_discipline);
    if (value) {
      normalized.pub_discipline = value;
    }
  }

  if (suggestedFilters.pub_ability) {
    const value = normalizeSelectFilterValue(suggestedFilters.pub_ability);
    if (value) {
      normalized.pub_ability = value;
    }
  }

  if (suggestedFilters.pub_groupType) {
    const value = normalizeSelectFilterValue(suggestedFilters.pub_groupType);
    if (value) {
      normalized.pub_groupType = value;
    }
  }

  if (suggestedFilters.pub_people) {
    const value = normalizePeopleSearchValue(suggestedFilters.pub_people);
    if (value) {
      normalized.pub_people = value;
    }
  }

  if (suggestedFilters.price) {
    const value = normalizePriceSearchRange(suggestedFilters.price);
    if (value) {
      normalized.price = value;
    }
  }

  if (suggestedFilters.dates) {
    const value = normalizeDateRangeSearchValue(suggestedFilters.dates);
    if (value) {
      normalized.dates = value;
    }
  }

  if (suggestedFilters.address) {
    const address = String(suggestedFilters.address).trim();
    if (address) {
      normalized.address = address;
    }
  }

  if (suggestedFilters.origin) {
    const origin =
      typeof suggestedFilters.origin === 'string'
        ? suggestedFilters.origin.trim()
        : formatLatLngSearchValue(suggestedFilters.origin);
    if (origin) {
      normalized.origin = origin;
    }
  }

  if (suggestedFilters.bounds) {
    const bounds =
      typeof suggestedFilters.bounds === 'string'
        ? suggestedFilters.bounds.trim()
        : formatBoundsSearchValue(suggestedFilters.bounds);
    if (bounds) {
      normalized.bounds = bounds;
    }
  }

  if (suggestedFilters.adults) {
    const adults = Number.parseInt(String(suggestedFilters.adults).trim(), 10);
    if (Number.isFinite(adults) && adults > 0) {
      normalized.adults = `${adults}`;
    }
  }

  if (suggestedFilters.children) {
    const children = Number.parseInt(String(suggestedFilters.children).trim(), 10);
    if (Number.isFinite(children) && children > 0) {
      normalized.children = `${children}`;
    }
  }

  if (suggestedFilters.keywords) {
    const keywords = String(suggestedFilters.keywords).trim();
    if (keywords) {
      normalized.keywords = keywords;
    }
  }

  return normalized;
};

const normalizePlannerSearchParams = plannerPreferences => {
  if (!plannerPreferences || typeof plannerPreferences !== 'object') {
    return {};
  }

  const normalized = {};
  const { location, timing, budget, group } = plannerPreferences;

  if (plannerPreferences.activity) {
    const value = normalizePlainFilterValue(plannerPreferences.activity);
    if (value) {
      normalized.pub_activity = value;
    }
  }

  if (plannerPreferences.discipline) {
    const value = normalizeSelectFilterValue(plannerPreferences.discipline);
    if (value) {
      normalized.pub_discipline = value;
    }
  }

  if (plannerPreferences.ability) {
    const value = normalizeSelectFilterValue(plannerPreferences.ability);
    if (value) {
      normalized.pub_ability = value;
    }
  }

  if (group?.groupType) {
    const value = normalizeSelectFilterValue(group.groupType);
    if (value) {
      normalized.pub_groupType = value;
    }
  }

  if (group?.people) {
    const value = normalizePeopleSearchValue(group.people);
    if (value) {
      normalized.pub_people = value;
    }
  }

  if (group?.adults) {
    const adults = Number.parseInt(String(group.adults).trim(), 10);
    if (Number.isFinite(adults) && adults > 0) {
      normalized.adults = `${adults}`;
    }
  }

  if (group?.children) {
    const children = Number.parseInt(String(group.children).trim(), 10);
    if (Number.isFinite(children) && children > 0) {
      normalized.children = `${children}`;
    }
  }

  if (timing?.dateFrom && timing?.dateTo) {
    const value = normalizeDateRangeSearchValue(`${timing.dateFrom}, ${timing.dateTo}`);
    if (value) {
      normalized.dates = value;
    }
  }

  if (budget && budget.mode !== 'flexible') {
    const min = normalizePriceSearchAmount(budget.min);
    const customMax = normalizePriceSearchAmount(budget.max);
    const max = customMax != null ? customMax : SEARCH_PRICE_MAX_MAJOR_UNIT;
    if ((min != null || customMax != null) && max > 0 && (min == null || max >= min)) {
      normalized.price = `${min || 0},${max}`;
    }
  }

  if (location && typeof location === 'object') {
    const cleanAddress = buildPlannerAddress(location);
    const origin = formatLatLngSearchValue(location.origin);
    const bounds = formatBoundsSearchValue(location.bounds);

    if (cleanAddress) {
      normalized.address = cleanAddress;
    }

    if (origin) {
      normalized.origin = origin;
    }

    if (bounds) {
      normalized.bounds = bounds;
    }
  }

  return normalized;
};

const buildSearchPageParams = (suggestedFilters, plannerPreferences) => {
  const suggestedParams = normalizeSuggestedSearchParams(suggestedFilters);
  const plannerParams = normalizePlannerSearchParams(plannerPreferences);

  return {
    ...suggestedParams,
    ...plannerParams,
  };
};

const formatFilterOptionLabel = (option, intl) => {
  if (!option) {
    return '';
  }

  const labelId = option.selectedLabel || option.label;
  if (typeof labelId === 'string' && labelId.includes('.')) {
    return intl.formatMessage({ id: labelId, defaultMessage: toReadableText(option.key) });
  }

  return typeof labelId === 'string' ? labelId : toReadableText(option.key);
};

const buildFilterOptionLookup = (filterId, filters, intl) =>
  findOptionsForSelectFilter(filterId, filters || []).reduce((acc, option) => {
    acc[option.key] = formatFilterOptionLabel(option, intl);
    return acc;
  }, {});

const toMajorUnitAmount = (amount, currency) => {
  if (typeof amount !== 'number' || !currency) {
    return null;
  }

  try {
    return amount / unitDivisor(currency);
  } catch (error) {
    return amount;
  }
};

const formatPrice = (amount, currency, locale) => {
  const majorUnitAmount = toMajorUnitAmount(amount, currency);
  if (majorUnitAmount == null || !currency) {
    return '';
  }

  return new Intl.NumberFormat(normalizeLocale(locale), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(majorUnitAmount);
};

const renderMarkdownInline = text => {
  // Handle bold (**text**) and links [text](url) in inline text
  const parts = String(text || '').split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);

  return parts.map((part, index) => {
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) {
      return <strong key={`b-${index}`}>{boldMatch[1]}</strong>;
    }

    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a key={`a-${index}`} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className={css.inlineLink}>
          {linkMatch[1]}
        </a>
      );
    }

    return <Fragment key={`t-${index}`}>{part}</Fragment>;
  });
};

const renderMarkdownText = text => {
  const raw = String(text || '');
  const lines = raw.split('\n');
  const blocks = [];
  let currentList = [];
  let currentListType = null; // 'ul' or 'ol'
  let blockIndex = 0;

  const flushList = () => {
    if (!currentList.length) return;
    const Tag = currentListType === 'ol' ? 'ol' : 'ul';
    blocks.push(
      <Tag key={`list-${blockIndex}`} className={currentListType === 'ol' ? css.mdOl : css.mdUl}>
        {currentList.map((item, i) => (
          <li key={`li-${blockIndex}-${i}`} className={css.mdLi}>{renderMarkdownInline(item)}</li>
        ))}
      </Tag>
    );
    blockIndex += 1;
    currentList = [];
    currentListType = null;
  };

  lines.forEach(line => {
    const ulMatch = line.match(/^[\s]*[-*•]\s+(.+)$/);
    const olMatch = line.match(/^[\s]*\d+[.)]\s+(.+)$/);

    if (ulMatch) {
      if (currentListType === 'ol') flushList();
      currentListType = 'ul';
      currentList.push(ulMatch[1]);
      return;
    }

    if (olMatch) {
      if (currentListType === 'ul') flushList();
      currentListType = 'ol';
      currentList.push(olMatch[1]);
      return;
    }

    flushList();

    const trimmed = line.trim();
    if (!trimmed) {
      blocks.push(<span key={`br-${blockIndex}`} className={css.mdBreak} />);
    } else {
      blocks.push(<span key={`p-${blockIndex}`} className={css.mdParagraph}>{renderMarkdownInline(trimmed)}</span>);
    }
    blockIndex += 1;
  });

  flushList();
  return blocks;
};

const getListingLocationText = listing =>
  listing.city || listing.activityLocation || listing.meetingLocation || '';

const getListingLanguagesText = listing =>
  Array.isArray(listing.languages) ? listing.languages.map(toReadableText).filter(Boolean).join(', ') : '';

const getListingTags = (listing, labelLookups) => {
  const disciplineTag =
    labelLookups.discipline?.[listing.discipline] || toReadableText(listing.discipline);

  return disciplineTag ? [disciplineTag] : [];
};

const getListingMeta = (listing, messagesByLocale, locale) => {
  const listingPrice = formatPrice(listing.priceAmount, listing.priceCurrency, locale);
  const nextSessionPrice = formatPrice(
    listing.nextSessionPriceAmount,
    listing.nextSessionPriceCurrency || listing.priceCurrency,
    locale
  );
  const locationText = getListingLocationText(listing);
  const languagesText = getListingLanguagesText(listing);
  const nextSessionInfoParts = [
    formatSessionDate(listing.nextSessionStartAt, locale),
    nextSessionPrice ? `${messagesByLocale.price}: ${nextSessionPrice}` : '',
    typeof listing.nextSessionAvailableSeats === 'number'
      ? `${messagesByLocale.seats}: ${listing.nextSessionAvailableSeats}`
      : '',
  ].filter(Boolean);

  return {
    listingPrice,
    locationText,
    languagesText,
    nextSessionInfo: nextSessionInfoParts.join(' | '),
  };
};

const getListingSupportText = (listing, messagesByLocale, locale) => {
  const { listingPrice, locationText, nextSessionInfo } = getListingMeta(listing, messagesByLocale, locale);
  const parts = [locationText, listingPrice, nextSessionInfo].filter(Boolean);
  return parts.slice(0, 2).join(' | ');
};

const getPrimaryListingFacts = (listing, messagesByLocale, locale) => {
  const { listingPrice, locationText, nextSessionInfo } = getListingMeta(listing, messagesByLocale, locale);
  const fallbackPrice = listingPrice ? `${messagesByLocale.priceFrom} ${listingPrice}` : '';
  return [locationText, nextSessionInfo || fallbackPrice].filter(Boolean).slice(0, 2);
};

const getSelectorListingSupportText = (listing, messagesByLocale, locale, labelLookups) => {
  const { listingPrice, locationText } = getListingMeta(listing, messagesByLocale, locale);
  return (
    locationText ||
    listingPrice ||
    labelLookups.discipline?.[listing.discipline] ||
    toReadableText(listing.discipline) ||
    ''
  );
};

const deriveListingHistory = (messages, locale, messagesByLocale) => {
  const deduped = new Map();

  for (let messageIndex = messages.length - 1; messageIndex >= 0; messageIndex -= 1) {
    const message = messages[messageIndex];
    if (message.role !== 'assistant' || !Array.isArray(message.listings) || !message.listings.length) {
      continue;
    }

    message.listings.forEach((listing, listingIndex) => {
      if (!listing?.id || deduped.has(listing.id)) {
        return;
      }

      deduped.set(listing.id, {
        id: listing.id,
        messageId: message.id,
        listingIndex,
        title: getLocalizedListingTitle(listing, locale) || listing.title,
        imageUrl: listing.imageUrl || '',
        reason: listing.reason || '',
        supportText: getListingSupportText(listing, messagesByLocale, locale),
        url: listing.url || '',
      });
    });
  }

  return Array.from(deduped.values());
};

const ListingMedia = props => {
  const { imageUrl, title, label } = props;

  return imageUrl ? (
    <div className={css.listingImageWrap}>
      <img className={css.listingImage} src={imageUrl} alt={title} />
    </div>
  ) : (
    <div className={classNames(css.listingImageWrap, css.listingImageFallback)} aria-hidden="true">
      <span className={css.listingImageFallbackLabel}>{label}</span>
    </div>
  );
};

ListingMedia.propTypes = {
  imageUrl: string,
  label: string.isRequired,
  title: string.isRequired,
};

ListingMedia.defaultProps = {
  imageUrl: '',
};

const LandingAIChatWidget = props => {
  const {
    locale,
    history,
    variant,
    defaultOpen,
    showTrigger,
    showSuggestedPrompts,
    contextPage,
    promptRequest,
    suggestedPrompts,
    resultsMode,
    onSuggestedListingsChange,
    focusRequest,
    initialChatState,
    inlineMaxHeight,
    disableAutoScroll,
    signupGateButtonLabel,
    signupGateDescription,
    signupSearch,
    sessionPersistKey,
  } = props;

  const isInline = variant === 'inline';
  const isExplorer = resultsMode === 'explorer';
  const intl = useIntl();
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const currentUser = useSelector(state => state.user?.currentUser);
  const isAuthenticated = !!currentUser;
  const copy = useMemo(() => getCrionAICopy(locale), [locale]);
  const messagesByLocale = useMemo(() => getMessagesForLocale(locale), [locale]);
  const searchFiltersConfig = useMemo(() => config?.search?.defaultFilters || [], [config]);
  const [restoredGateState] = useState(() => readSignupGateChatState());
  // When no initialChatState prop is provided (e.g. LandingPage), restore from sessionStorage
  // so the chat survives navigating away and back within the same browser session.
  const [restoredSession] = useState(() =>
    !initialChatState ? readPersistedChatSession(sessionPersistKey) : null
  );
  const initialRestoredState = useMemo(
    () => buildInitialChatState(initialChatState || restoredSession?.chatState, messagesByLocale.greeting, restoredGateState),
    [initialChatState, restoredSession, messagesByLocale.greeting, restoredGateState]
  );
  const [isOpen, setIsOpen] = useState(defaultOpen || isInline || !!restoredGateState?.wasOpen);
  const [isLoading, setIsLoading] = useState(false);
  const [draft, setDraft] = useState(initialRestoredState.draft);
  const [errorMessage, setErrorMessage] = useState('');
  const [chatSessionId, setChatSessionId] = useState(initialRestoredState.chatSessionId);
  const [messages, setMessages] = useState(initialRestoredState.messages);
  const [activeListingIndexByMessageId, setActiveListingIndexByMessageId] = useState(
    initialRestoredState.activeListingIndexByMessageId
  );
  const [stripOverflowByMessageId, setStripOverflowByMessageId] = useState({});
  const [isMobilePicksOpen, setIsMobilePicksOpen] = useState(false);
  const [isManualRequestOpen, setIsManualRequestOpen] = useState(false);
  const [manualRequestStatus, setManualRequestStatus] = useState('idle');
  const [manualRequestError, setManualRequestError] = useState('');
  const [manualRequestForm, setManualRequestForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    notes: '',
  });
  const messagesContainerRef = useRef(null);
  const loadingMessageRef = useRef(null);
  const latestAssistantRef = useRef(null);
  // Initialise with the already-answered promptRequest id so the widget never re-fires a
  // prompt that is already visible in the restored chat history.
  //  • LandingPage path: session was read directly from sessionStorage (restoredSession).
  //  • CrionAIPage path: chat was restored via the initialChatState prop; the matching
  //    promptRequest prop is passed alongside it — detect this by checking for real messages.
  const lastPromptRequestIdRef = useRef(
    restoredSession?.promptRequest?.id ??
    (initialRestoredState.messages.length > 1 && promptRequest?.id ? promptRequest.id : null)
  );
  const lastFocusRequestIdRef = useRef(null);
  const lastInitialChatStateIdRef = useRef(initialChatState?.id || null);
  const messageRefs = useRef({});
  const listingStripRefs = useRef({});
  const labelLookups = useMemo(
    () => ({
      activity: buildFilterOptionLookup('activity', searchFiltersConfig, intl),
      discipline: buildFilterOptionLookup('discipline', searchFiltersConfig, intl),
      ability: buildFilterOptionLookup('ability', searchFiltersConfig, intl),
      groupType: buildFilterOptionLookup('groupType', searchFiltersConfig, intl),
    }),
    [intl, searchFiltersConfig]
  );

  const promptExamples = useMemo(() => {
    if (Array.isArray(suggestedPrompts) && suggestedPrompts.length) {
      return suggestedPrompts.slice(0, 4);
    }

    return pickRandomCrionAIPrompts(copy.examples, 4);
  }, [copy.examples, suggestedPrompts]);

  const isPanelOpen = isInline || isOpen;
  const panelInlineStyle =
    isInline && Number.isFinite(inlineMaxHeight) && inlineMaxHeight > 0
      ? {
          height: `${Math.round(inlineMaxHeight)}px`,
          maxHeight: `${Math.round(inlineMaxHeight)}px`,
        }
      : undefined;
  const hasConversationStarted =
    messages.length > 1 || (messages[0] && messages[0].content !== messagesByLocale.greeting);
  const userMessageCount = useMemo(() => messages.filter(message => message.role === 'user').length, [messages]);
  const latestAssistantMessage = useMemo(
    () =>
      [...messages]
        .reverse()
        .find(message => message.role === 'assistant' && message.content !== messagesByLocale.greeting) || null,
    [messages, messagesByLocale.greeting]
  );
  const hasAssistantReply = !!latestAssistantMessage;
  const isSignupGated = !isAuthenticated && userMessageCount >= FREE_QUESTIONS_LIMIT && hasAssistantReply;
  const signupGateDescriptionText = signupGateDescription || messagesByLocale.signupGateDescription;
  const signupGateButtonText = signupGateButtonLabel || messagesByLocale.signupGateButton;
  const latestAssistantListings = useMemo(
    () => (Array.isArray(latestAssistantMessage?.listings) ? latestAssistantMessage.listings : []),
    [latestAssistantMessage]
  );
  const shouldOfferManualRequest =
    hasConversationStarted && !isLoading && (userMessageCount >= 2 || !latestAssistantListings.length);
  const tailoredRequestFormLink = config?.tailoredActivitiesFormLink || '';

  const listingHistory = useMemo(
    () => deriveListingHistory(messages, locale, messagesByLocale),
    [locale, messages, messagesByLocale]
  );

  const scrollSelectorChipIntoView = useCallback((messageId, listingIndex, focusChip = false) => {
    scheduleFrame(() => {
      const stripNode = listingStripRefs.current[messageId];
      const selectorNode = stripNode?.querySelector(`[data-listing-index="${listingIndex}"]`);

      if (selectorNode?.scrollIntoView) {
        selectorNode.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }

      if (focusChip && selectorNode?.focus) {
        selectorNode.focus();
      }
    });
  }, []);

  const activateListing = useCallback(
    (messageId, listingIndex, options = {}) => {
      const { focusChip = false, scrollMessage = false } = options;

      setActiveListingIndexByMessageId(prev => ({
        ...prev,
        [messageId]: listingIndex,
      }));

      scrollSelectorChipIntoView(messageId, listingIndex, focusChip);

      if (scrollMessage) {
        scheduleFrame(() => {
          const messageNode = messageRefs.current[messageId];
          if (messageNode?.scrollIntoView) {
            messageNode.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        });
      }
    },
    [scrollSelectorChipIntoView]
  );

  const focusListingEntry = useCallback(
    entry => {
      if (!entry) {
        return;
      }

      activateListing(entry.messageId, entry.listingIndex, {
        focusChip: true,
        scrollMessage: true,
      });
    },
    [activateListing]
  );

  const updateSelectorOverflow = useCallback(() => {
    const nextState = {};

    Object.entries(listingStripRefs.current).forEach(([messageId, node]) => {
      if (!node) {
        return;
      }

      nextState[messageId] = node.scrollWidth > node.clientWidth + 4;
    });

    setStripOverflowByMessageId(nextState);
  }, []);

  const resetConversation = () => {
    setDraft('');
    setErrorMessage('');
    setActiveListingIndexByMessageId({});
    setStripOverflowByMessageId({});
    setIsMobilePicksOpen(false);
    setIsManualRequestOpen(false);
    setManualRequestStatus('idle');
    setManualRequestError('');
    setManualRequestForm({
      firstName: '',
      lastName: '',
      email: '',
      notes: '',
    });
    setChatSessionId(createChatSessionId());
    setMessages([createMessage('assistant', messagesByLocale.greeting)]);
    // Clear the persisted session so a fresh chat is not overridden on the next mount.
    if (sessionPersistKey && typeof window !== 'undefined' && window.sessionStorage) {
      try { window.sessionStorage.removeItem(sessionPersistKey); } catch { /* Ignore. */ }
    }
  };

  const continueInCrionAIPage = useCallback(() => {
    const targetPath = createResourceLocatorString('CrionAIPage', routeConfiguration, {}, {});
    const handoffState = createChatHandoffState(messages, draft, activeListingIndexByMessageId, chatSessionId);

    if (typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem(AI_CHAT_HANDOFF_STORAGE_KEY, JSON.stringify(handoffState));
      } catch (error) {
        // Ignore storage failures and rely on route state.
      }
    }

    if (history?.push) {
      history.push(targetPath, {
        aiChatHandoffId: handoffState.id,
        aiChatState: handoffState,
      });
    } else {
      window.location.assign(targetPath);
    }

    if (!isInline) {
      setIsOpen(false);
    }
  }, [activeListingIndexByMessageId, chatSessionId, draft, history, isInline, messages, routeConfiguration]);

  const saveChatStateForAuth = useCallback(() => {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    const state = {
      ...createChatHandoffState(messages, draft, activeListingIndexByMessageId, chatSessionId),
      wasOpen: isOpen,
      scrollY: window.scrollY ?? 0,
    };
    try {
      window.sessionStorage.setItem(AI_CHAT_SIGNUP_GATE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage failures.
    }

    // Persist the planner promptRequest so CrionAIPage can restore it after auth
    if (promptRequest) {
      try {
        window.sessionStorage.setItem('crion-ai-planner-request', JSON.stringify(promptRequest));
      } catch {
        // Ignore storage failures.
      }
    }
  }, [activeListingIndexByMessageId, chatSessionId, draft, isOpen, messages, promptRequest]);

  // Auto-save chat state to sessionStorage so returning to CrionAIPage restores the session.
  // Only save when the AI has finished responding (isLoading=false) to avoid storing partial replies.
  useEffect(() => {
    if (!sessionPersistKey || typeof window === 'undefined' || !window.sessionStorage) return;
    if (isLoading || messages.length <= 1) return;
    const persistState = {
      chatState: createChatHandoffState(messages, draft, activeListingIndexByMessageId, chatSessionId),
      ...(promptRequest ? { promptRequest } : {}),
    };
    try {
      window.sessionStorage.setItem(sessionPersistKey, JSON.stringify(persistState));
    } catch {
      // Ignore storage failures.
    }
  }, [messages, isLoading, sessionPersistKey, draft, activeListingIndexByMessageId, chatSessionId, promptRequest]);

  const navigateToSignup = useCallback(() => {
    saveChatStateForAuth();
    const signupPath = createResourceLocatorString('SignupPage', routeConfiguration, {}, {});
    const search = signupSearch
      ? signupSearch.startsWith('?')
        ? signupSearch
        : `?${signupSearch}`
      : '';
    const currentLocation =
      history?.location ||
      (typeof window !== 'undefined'
        ? {
            pathname: window.location.pathname,
            search: window.location.search || '',
            hash: window.location.hash || '',
          }
        : { pathname: '/', search: '', hash: '' });
    const from = `${currentLocation.pathname}${currentLocation.search || ''}${currentLocation.hash || ''}`;
    const modalState = {
      modal: true,
      from,
      backgroundLocation: {
        pathname: currentLocation.pathname,
        search: currentLocation.search || '',
        hash: currentLocation.hash || '',
      },
    };
    if (history?.push) {
      history.push(`${signupPath}${search}`, modalState);
    } else if (typeof window !== 'undefined') {
      window.location.assign(`${signupPath}${search}`);
    }
  }, [history, routeConfiguration, saveChatStateForAuth, signupSearch]);

  const navigateToLogin = useCallback(() => {
    saveChatStateForAuth();
    const loginPath = createResourceLocatorString('LoginPage', routeConfiguration, {}, {});
    const currentLocation =
      history?.location ||
      (typeof window !== 'undefined'
        ? {
            pathname: window.location.pathname,
            search: window.location.search || '',
            hash: window.location.hash || '',
          }
        : { pathname: '/', search: '', hash: '' });
    const from = `${currentLocation.pathname}${currentLocation.search || ''}${currentLocation.hash || ''}`;
    const modalState = {
      modal: true,
      from,
      backgroundLocation: {
        pathname: currentLocation.pathname,
        search: currentLocation.search || '',
        hash: currentLocation.hash || '',
      },
    };
    if (history?.push) {
      history.push(loginPath, modalState);
    } else if (typeof window !== 'undefined') {
      window.location.assign(loginPath);
    }
  }, [history, routeConfiguration, saveChatStateForAuth]);

  const applySuggestedFilters = (suggestedFilters, plannerPreferences) => {
    const normalizedFilters = buildSearchPageParams(suggestedFilters, plannerPreferences);

    if (!Object.keys(normalizedFilters).length) {
      return;
    }

    const paths = createURLPaths(normalizedFilters);
    const targetPath = createResourceLocatorString('SearchPage', routeConfiguration, paths, normalizedFilters);
    window.open(targetPath, '_blank', 'noopener,noreferrer');
  };

  const scrollSelectorStrip = useCallback(
    (messageId, direction) => {
      const stripNode = listingStripRefs.current[messageId];
      if (!stripNode?.scrollBy) {
        return;
      }

      stripNode.scrollBy({
        left: direction * Math.max(stripNode.clientWidth * 0.72, 220),
        behavior: 'smooth',
      });

      scheduleFrame(updateSelectorOverflow);
    },
    [updateSelectorOverflow]
  );

  const sendMessage = useCallback(
    async (messageOverride, options = {}) => {
      const message = typeof messageOverride === 'string' ? messageOverride.trim() : draft.trim();

      if (!message || isLoading) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Crion AI chat send skipped', {
            reason: !message ? 'empty_message' : 'loading',
            isLoading,
          });
        }
        return;
      }

      const chatHistory = toVisibleChatHistory(messages, messagesByLocale.greeting);
      const hasAssistantReply = messages.some(
        message =>
          message.role === 'assistant' &&
          message.content &&
          message.content !== messagesByLocale.greeting
      );
      const hasListingSuggestions = messages.some(
        message => message.role === 'assistant' && Array.isArray(message.listings) && message.listings.length > 0
      );
      const isFollowUp = hasAssistantReply && !options.isPlannerPrompt;

      // Check signup gate: allow up to FREE_QUESTIONS_LIMIT answered questions for anonymous users.
      if (!isAuthenticated && userMessageCount >= FREE_QUESTIONS_LIMIT && hasAssistantReply) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Crion AI chat send skipped', {
            reason: 'signup_gate',
            chatSessionId,
            userMessageCount,
            hasAssistantReply,
          });
        }
        return;
      }

      if (process.env.NODE_ENV !== 'production') {
        console.warn('Crion AI chat send start', {
          chatSessionId,
          contextPage,
          isPlannerPrompt: !!options.isPlannerPrompt,
          isFollowUp,
          historyCount: chatHistory.length,
        });
      }

      const userMessage = createMessage('user', message, {
        displayContent:
          typeof options.displayContent === 'string' && options.displayContent
            ? options.displayContent
            : undefined,
        isPlannerPrompt: !!options.isPlannerPrompt,
        plannerPreferences:
          options.context?.plannerPreferences && typeof options.context.plannerPreferences === 'object'
            ? options.context.plannerPreferences
            : null,
      });

      setDraft('');
      setErrorMessage('');
      setManualRequestError('');
      setManualRequestStatus('idle');
      setIsManualRequestOpen(false);
      setMessages(prev => [...prev, userMessage]);
      setIsLoading(true);

      try {
        const { data } = await api.aiChat.sendMessage({
          message,
          locale,
          history: chatHistory,
          context: {
            page: contextPage,
            chatSessionId,
            isFollowUp,
            hasAssistantReply,
            hasListingSuggestions,
            visibleHistoryCount: chatHistory.length,
            ...(options.context && typeof options.context === 'object' ? options.context : {}),
          },
        });

        const reply = data?.reply || messagesByLocale.fallback;
        const listings = Array.isArray(data?.listings) ? data.listings : [];
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Crion AI chat response received', {
            chatSessionId,
            hasReply: !!reply,
            listingsCount: listings.length,
            scopeLabel: data?.scopeLabel,
          });
        }
        const assistantMessage = createMessage('assistant', reply, {
          listings,
          suggestedFilters: data?.suggestedFilters || null,
          plannerPreferences:
            options.context?.plannerPreferences && typeof options.context.plannerPreferences === 'object'
              ? options.context.plannerPreferences
              : null,
        });

        setMessages(prev => [...prev, assistantMessage]);
        if (listings.length) {
          setActiveListingIndexByMessageId(prev => ({
            ...prev,
            [assistantMessage.id]: 0,
          }));
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Crion AI chat request failed', {
            chatSessionId,
            status: error?.response?.status,
            data: error?.response?.data,
            message: error?.message,
          });
        }
        setErrorMessage(messagesByLocale.fallback);
      } finally {
        setIsLoading(false);
      }
    },
    [
      contextPage,
      draft,
      chatSessionId,
      isAuthenticated,
      isLoading,
      locale,
      messages,
      messagesByLocale.fallback,
      messagesByLocale.greeting,
      userMessageCount,
    ]
  );

  const sendManualRequest = useCallback(
    async event => {
      event.preventDefault();

      const payload = {
        firstName: trimText(manualRequestForm.firstName, 64),
        lastName: trimText(manualRequestForm.lastName, 128),
        email: trimText(manualRequestForm.email, 180),
        message: buildManualRequestMessage({
          messages,
          contextPage,
          locale,
          extraNotes: manualRequestForm.notes,
          latestSuggestedListings: latestAssistantListings,
        }),
      };

      if (!payload.firstName || !payload.lastName || !payload.email || !payload.message) {
        setManualRequestError(messagesByLocale.manualRequestError);
        return;
      }

      setManualRequestStatus('sending');
      setManualRequestError('');

      try {
        await api.contactUs.contact(payload);
        setManualRequestStatus('sent');
        setIsManualRequestOpen(false);
      } catch (error) {
        setManualRequestStatus('idle');
        setManualRequestError(messagesByLocale.manualRequestError);
      }
    },
    [contextPage, latestAssistantListings, locale, manualRequestForm, messages, messagesByLocale.manualRequestError]
  );

  useEffect(() => {
    if (disableAutoScroll) {
      return;
    }

    if (restoredGateState?.scrollY != null && typeof window !== 'undefined') {
      window.scrollTo({ top: restoredGateState.scrollY, behavior: 'instant' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialChatState?.id && initialChatState.id !== lastInitialChatStateIdRef.current) {
      lastInitialChatStateIdRef.current = initialChatState.id;
      const restoredState = buildInitialChatState(initialChatState, messagesByLocale.greeting, null);
      setDraft(restoredState.draft);
      setErrorMessage('');
      setChatSessionId(restoredState.chatSessionId);
      setMessages(restoredState.messages);
      setActiveListingIndexByMessageId(restoredState.activeListingIndexByMessageId);
      setStripOverflowByMessageId({});
      setIsMobilePicksOpen(false);
      setIsLoading(false);

      if (!isInline) {
        setIsOpen(true);
      }
    }
  }, [initialChatState, isInline, messagesByLocale.greeting]);

  useEffect(() => {
    if (isInline && !isOpen) {
      setIsOpen(true);
    }
  }, [isInline, isOpen]);

  useEffect(() => {
    if (
      messages.length === 1 &&
      messages[0].role === 'assistant' &&
      messages[0].content !== messagesByLocale.greeting
    ) {
      setMessages([createMessage('assistant', messagesByLocale.greeting)]);
    }
  }, [messages, messagesByLocale.greeting]);

  useEffect(() => {
    if (!promptRequest?.prompt || promptRequest.id === lastPromptRequestIdRef.current) {
      return;
    }

    lastPromptRequestIdRef.current = promptRequest.id;
    if (!isInline) {
      setIsOpen(true);
    }

    sendMessage(promptRequest.prompt, {
      displayContent: promptRequest.displayMessage || 'Preferences sent to Crion AI',
      isPlannerPrompt: true,
      context: promptRequest.context,
    });
  }, [isInline, promptRequest, sendMessage]);

  useEffect(() => {
    if (disableAutoScroll) {
      return;
    }

    if (!isPanelOpen || !isLoading || !loadingMessageRef.current) {
      return;
    }

    loadingMessageRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [disableAutoScroll, isLoading, isPanelOpen]);

  useEffect(() => {
    if (disableAutoScroll) {
      return;
    }

    if (!isPanelOpen || !messagesContainerRef.current || isLoading) {
      return;
    }

    if (latestAssistantRef.current) {
      latestAssistantRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [disableAutoScroll, messages, isLoading, isPanelOpen]);

  useEffect(() => {
    updateSelectorOverflow();

    if (typeof window === 'undefined') {
      return undefined;
    }

    window.addEventListener('resize', updateSelectorOverflow);
    return () => window.removeEventListener('resize', updateSelectorOverflow);
  }, [messages, updateSelectorOverflow]);

  useEffect(() => {
    if (!onSuggestedListingsChange) {
      return;
    }

    if (!isExplorer) {
      onSuggestedListingsChange([]);
      return;
    }

    onSuggestedListingsChange(listingHistory);
  }, [isExplorer, listingHistory, onSuggestedListingsChange]);

  useEffect(() => {
    if (!focusRequest?.id || focusRequest.id === lastFocusRequestIdRef.current) {
      return;
    }

    lastFocusRequestIdRef.current = focusRequest.id;

    const targetEntry = listingHistory.find(entry => {
      if (focusRequest.messageId && focusRequest.listingId) {
        return entry.messageId === focusRequest.messageId && entry.id === focusRequest.listingId;
      }

      return entry.id === focusRequest.listingId;
    });

    if (!targetEntry) {
      return;
    }

    setIsMobilePicksOpen(true);
    focusListingEntry(targetEntry);
  }, [focusListingEntry, focusRequest, listingHistory]);

  if (!ENABLED) {
    return null;
  }

  if (!isPanelOpen && !showTrigger) {
    return null;
  }

  return (
    <div className={classNames(css.root, { [css.rootInline]: isInline })}>
      {!isPanelOpen ? (
        <button type="button" className={css.triggerButton} onClick={() => setIsOpen(true)}>
          <span className={css.triggerSignal} aria-hidden="true" />
          <span className={css.triggerContent}>
            <span className={css.triggerEyebrow}>{messagesByLocale.triggerEyebrow}</span>
            <span className={css.triggerTitle}>{messagesByLocale.trigger}</span>
            <span className={css.triggerSubline}>{messagesByLocale.triggerSubline}</span>
          </span>
        </button>
      ) : (
        <div
          className={classNames(css.panel, {
            [css.panelLoading]: isLoading,
            [css.panelInline]: isInline,
          })}
          style={panelInlineStyle}
        >
          <div className={classNames(css.header, { [css.headerInline]: isInline })}>
            <div className={css.headerTitleGroup}>
              <strong>{messagesByLocale.title}</strong>
              {isInline ? <span className={css.headerHint}>{copy.pageIntro}</span> : null}
            </div>
            <div className={css.headerActions}>
              {hasConversationStarted ? (
                <button type="button" className={css.headerActionButton} onClick={resetConversation}>
                  {messagesByLocale.newChat}
                </button>
              ) : null}
              {!isInline ? (
                <button type="button" className={css.closeButton} onClick={() => setIsOpen(false)}>
                  {messagesByLocale.close}
                </button>
              ) : null}
            </div>
          </div>

          {isExplorer ? (
            <div className={css.mobilePicksShell}>
              <button
                type="button"
                className={css.mobilePicksToggle}
                onClick={() => setIsMobilePicksOpen(prev => !prev)}
                aria-expanded={isMobilePicksOpen}
              >
                <span>{messagesByLocale.picksTitle}</span>
                <span className={css.mobilePicksCount}>{listingHistory.length}</span>
              </button>
              {isMobilePicksOpen ? (
                listingHistory.length ? (
                  <div className={css.mobilePicksList}>
                    {listingHistory.map(entry => (
                      <button
                        key={`${entry.messageId}-${entry.id}`}
                        type="button"
                        className={css.mobilePicksItem}
                        onClick={() => focusListingEntry(entry)}
                      >
                        <ListingMedia
                          imageUrl={entry.imageUrl}
                          label={messagesByLocale.noImage}
                          title={entry.title}
                        />
                        <span className={css.mobilePicksContent}>
                          <span className={css.mobilePicksTitle}>{entry.title}</span>
                          {entry.supportText ? (
                            <span className={css.mobilePicksMeta}>{entry.supportText}</span>
                          ) : null}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className={css.mobilePicksEmpty}>{messagesByLocale.picksEmpty}</p>
                )
              ) : null}
            </div>
          ) : null}

          <div className={css.messages} ref={messagesContainerRef}>
            {!hasConversationStarted ? (
              <section className={css.welcomeCard}>
                <span className={css.welcomeEyebrow}>{copy.widgetWelcomeEyebrow}</span>
                <h3 className={css.welcomeTitle}>{copy.widgetWelcomeTitle}</h3>
                <p className={css.welcomeDescription}>{copy.widgetWelcomeDescription}</p>

                <ul className={css.welcomeList}>
                  {copy.widgetWelcomePoints.map(item => (
                    <li key={item} className={css.welcomeListItem}>
                      {item}
                    </li>
                  ))}
                </ul>

                {showSuggestedPrompts ? (
                  <>
                    <div className={css.promptIntro}>
                      <strong>{copy.promptSectionTitle}</strong>
                    </div>
                    <div className={css.promptGrid}>
                      {promptExamples.map(example => (
                        <button
                          key={example.id || example.prompt}
                          type="button"
                          className={css.promptChip}
                          onClick={() => sendMessage(example.prompt)}
                        >
                          <span className={css.promptChipLabel}>{example.label}</span>
                          <span className={css.promptChipText}>{example.prompt}</span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}
              </section>
            ) : null}

            {messages.map((message, index) => {
              const listings = Array.isArray(message.listings) ? message.listings : [];
              const normalizedSuggestedFilters = buildSearchPageParams(
                message.suggestedFilters,
                message.plannerPreferences
              );
              const activeListingIndex = listings.length
                ? Math.min(activeListingIndexByMessageId[message.id] || 0, listings.length - 1)
                : 0;
              const activeListing = listings[activeListingIndex] || null;
              const activeListingTitle = activeListing
                ? getLocalizedListingTitle(activeListing, locale) || activeListing.title
                : '';
              const activeListingTags = activeListing ? getListingTags(activeListing, labelLookups) : [];
              const activeListingFacts = activeListing
                ? getPrimaryListingFacts(activeListing, messagesByLocale, locale)
                : [];
              const isLatestAssistant = index === messages.length - 1 && message.role === 'assistant';
              const displayedMessageContent = message.displayContent || message.content;

              return (
                <div
                  key={message.id}
                  ref={node => {
                    if (node) {
                      messageRefs.current[message.id] = node;
                    }

                    if (isLatestAssistant) {
                      latestAssistantRef.current = node;
                    }
                  }}
                  data-role={message.role}
                  className={classNames(css.message, {
                    [css.assistantMessage]: message.role === 'assistant',
                    [css.userMessage]: message.role === 'user',
                    [css.plannerPromptMessage]: message.isPlannerPrompt,
                  })}
                >
                  <div className={css.messageText}>{renderMarkdownText(displayedMessageContent)}</div>

                  {message.role === 'assistant' && activeListing ? (
                    <div className={css.listingSuggestions}>
                      <article className={css.listingCard}>
                        <ListingMedia
                          imageUrl={activeListing.imageUrl}
                          label={messagesByLocale.noImage}
                          title={activeListingTitle}
                        />

                        <div className={css.listingBody}>
                          <div className={css.listingTitle}>{activeListingTitle}</div>
                          {activeListing.reason ? (
                            <div className={css.listingReasonBlock}>
                              <span className={css.listingReasonLabel}>{messagesByLocale.whyChosen}</span>
                              <div className={css.listingReason}>{activeListing.reason}</div>
                            </div>
                          ) : null}

                          {activeListingTags.length ? (
                            <div className={css.listingTags}>
                              {activeListingTags.map(tag => (
                                <span key={`${activeListing.id}-${tag}`} className={css.listingTag}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          ) : null}

                          {activeListingFacts.length ? (
                            <div className={css.listingFacts}>
                              {activeListingFacts.map(fact => (
                                <span key={`${activeListing.id}-${fact}`} className={css.listingFact}>
                                  {fact}
                                </span>
                              ))}
                            </div>
                          ) : null}

                          {activeListing.url ? (
                            <div className={css.listingFooter}>
                              <a
                                href={activeListing.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={css.listingLink}
                              >
                                {messagesByLocale.openListing}
                              </a>
                            </div>
                          ) : null}
                        </div>
                      </article>

                      {listings.length > 1 ? (
                        <div className={css.selectorSection}>
                          <div className={css.selectorHeader}>
                            <span className={css.selectorTitle}>
                              {listings.length} {messagesByLocale.resultsCount}
                            </span>
                            {stripOverflowByMessageId[message.id] ? (
                              <div className={css.selectorControls}>
                                <button
                                  type="button"
                                  className={css.selectorControl}
                                  onClick={() => scrollSelectorStrip(message.id, -1)}
                                  aria-label={messagesByLocale.previous}
                                >
                                  <span aria-hidden="true">&lsaquo;</span>
                                </button>
                                <button
                                  type="button"
                                  className={css.selectorControl}
                                  onClick={() => scrollSelectorStrip(message.id, 1)}
                                  aria-label={messagesByLocale.next}
                                >
                                  <span aria-hidden="true">&rsaquo;</span>
                                </button>
                              </div>
                            ) : null}
                          </div>

                          <div
                            ref={node => {
                              listingStripRefs.current[message.id] = node;
                            }}
                            className={css.selectorStrip}
                            role="group"
                            aria-label={messagesByLocale.selectorLabel}
                          >
                            {listings.map((listing, listingIndex) => {
                              const selectorTitle = getLocalizedListingTitle(listing, locale) || listing.title;
                              return (
                              <button
                                key={`${message.id}-${listing.id}`}
                                type="button"
                                className={classNames(css.selectorCard, {
                                  [css.selectorCardActive]: listingIndex === activeListingIndex,
                                })}
                                onClick={() => activateListing(message.id, listingIndex)}
                                aria-pressed={listingIndex === activeListingIndex}
                                data-listing-index={listingIndex}
                              >
                                <ListingMedia
                                  imageUrl={listing.imageUrl}
                                  label={messagesByLocale.noImage}
                                  title={selectorTitle}
                                />
                                <span className={css.selectorCardText}>
                                  <span className={css.selectorCardTitle}>{selectorTitle}</span>
                                  <span className={css.selectorCardMeta}>
                                    {getSelectorListingSupportText(
                                      listing,
                                      messagesByLocale,
                                      locale,
                                      labelLookups
                                    ) || listing.reason}
                                  </span>
                                </span>
                              </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {message.role === 'assistant' &&
                  (Object.keys(normalizedSuggestedFilters).length || (isLatestAssistant && contextPage !== 'crion-ai')) ? (
                    <div className={css.messageActionRow}>
                      {Object.keys(normalizedSuggestedFilters).length ? (
                        <button
                          type="button"
                          className={css.applyFiltersButton}
                          onClick={() =>
                            applySuggestedFilters(message.suggestedFilters, message.plannerPreferences)
                          }
                        >
                          {messagesByLocale.applyFilters}
                        </button>
                      ) : (
                        <span />
                      )}

                      {isLatestAssistant && contextPage !== 'crion-ai' ? (
                        <button
                          type="button"
                          className={css.continueInAiButton}
                          onClick={continueInCrionAIPage}
                        >
                          {messagesByLocale.continueInAiPage}
                        </button>
                      ) : null}
                    </div>
                  ) : null}

                  {message.role === 'assistant' && isLatestAssistant && shouldOfferManualRequest ? (
                    <div className={css.manualRequestShell}>
                      <div className={css.manualRequestCard}>
                        <div className={css.manualRequestHeader}>
                          <strong>{messagesByLocale.manualRequestPrompt}</strong>
                          <p>{messagesByLocale.manualRequestDescription}</p>
                        </div>

                        {manualRequestStatus === 'sent' ? (
                          <div className={css.manualRequestSuccess}>{messagesByLocale.manualRequestSuccess}</div>
                        ) : isManualRequestOpen ? (
                          <form className={css.manualRequestForm} onSubmit={sendManualRequest}>
                            <div className={css.manualRequestFieldGrid}>
                              <input
                                type="text"
                                className={css.manualRequestInput}
                                value={manualRequestForm.firstName}
                                onChange={event =>
                                  setManualRequestForm(prev => ({ ...prev, firstName: event.target.value }))
                                }
                                placeholder={messagesByLocale.manualRequestFirstName}
                                maxLength={64}
                                required
                              />
                              <input
                                type="text"
                                className={css.manualRequestInput}
                                value={manualRequestForm.lastName}
                                onChange={event =>
                                  setManualRequestForm(prev => ({ ...prev, lastName: event.target.value }))
                                }
                                placeholder={messagesByLocale.manualRequestLastName}
                                maxLength={128}
                                required
                              />
                            </div>

                            <input
                              type="email"
                              className={css.manualRequestInput}
                              value={manualRequestForm.email}
                              onChange={event =>
                                setManualRequestForm(prev => ({ ...prev, email: event.target.value }))
                              }
                              placeholder={messagesByLocale.manualRequestEmail}
                              maxLength={180}
                              required
                            />

                            <textarea
                              className={css.manualRequestTextarea}
                              rows={3}
                              value={manualRequestForm.notes}
                              onChange={event =>
                                setManualRequestForm(prev => ({ ...prev, notes: event.target.value }))
                              }
                              placeholder={messagesByLocale.manualRequestNotesPlaceholder}
                              maxLength={700}
                            />

                            {manualRequestError ? (
                              <div className={css.manualRequestError}>{manualRequestError}</div>
                            ) : null}

                            <div className={css.manualRequestActions}>
                              <button
                                type="submit"
                                className={css.manualRequestSubmit}
                                disabled={manualRequestStatus === 'sending'}
                              >
                                {messagesByLocale.manualRequestSubmit}
                              </button>
                              <button
                                type="button"
                                className={css.manualRequestSecondary}
                                onClick={() => {
                                  setIsManualRequestOpen(false);
                                  setManualRequestError('');
                                }}
                              >
                                {messagesByLocale.manualRequestCancel}
                              </button>
                            </div>
                          </form>
                        ) : (
                          <div className={css.manualRequestActions}>
                            <button
                              type="button"
                              className={css.manualRequestSubmit}
                              onClick={() => {
                                setManualRequestStatus('idle');
                                setManualRequestError('');
                                setIsManualRequestOpen(true);
                              }}
                            >
                              {messagesByLocale.manualRequestButton}
                            </button>
                            {tailoredRequestFormLink ? (
                              <a
                                href={tailoredRequestFormLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={css.manualRequestSecondary}
                              >
                                {messagesByLocale.manualRequestFallbackLink}
                              </a>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {isLoading ? (
              <div ref={loadingMessageRef} className={classNames(css.message, css.assistantMessage, css.loadingBubble)}>
                <span className={css.spinner} />
                <span>{messagesByLocale.loading}</span>
              </div>
            ) : null}
          </div>

          {errorMessage ? <div className={css.error}>{errorMessage}</div> : null}

          {isSignupGated ? (
            <div className={css.signupGateCard}>
              <h3 className={css.signupGateTitle}>{messagesByLocale.signupGateTitle}</h3>
              <p className={css.signupGateDescription}>{signupGateDescriptionText}</p>
              <button
                type="button"
                className={css.signupGateButton}
                onClick={navigateToSignup}
              >
                {signupGateButtonText}
              </button>
              <button
                type="button"
                className={css.signupGateLogin}
                onClick={navigateToLogin}
              >
                {messagesByLocale.signupGateLogin}
              </button>
            </div>
          ) : (
            <form
              className={classNames(css.inputForm, { [css.inputFormInline]: isInline })}
              onSubmit={event => {
                event.preventDefault();
                sendMessage();
              }}
            >
              <textarea
                className={css.input}
                rows={isInline ? 3 : 2}
                value={draft}
                onChange={event => setDraft(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder={messagesByLocale.placeholder}
              />
              <button type="submit" className={css.sendButton} disabled={isLoading || !draft.trim()}>
                {messagesByLocale.send}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

LandingAIChatWidget.defaultProps = {
  contextPage: 'landing',
  defaultOpen: false,
  disableAutoScroll: false,
  focusRequest: null,
  history: null,
  initialChatState: null,
  inlineMaxHeight: null,
  locale: 'en-US',
  onSuggestedListingsChange: null,
  promptRequest: null,
  resultsMode: 'compact',
  showSuggestedPrompts: true,
  showTrigger: true,
  signupGateButtonLabel: null,
  signupGateDescription: null,
  signupSearch: '',
  suggestedPrompts: null,
  variant: 'floating',
};

LandingAIChatWidget.propTypes = {
  contextPage: string,
  defaultOpen: bool,
  disableAutoScroll: bool,
  focusRequest: shape({
    id: string,
    listingId: string,
    listingIndex: number,
    messageId: string,
  }),
  history: shape({
    push: func,
  }),
  initialChatState: shape({
    activeListingIndexByMessageId: object,
    chatSessionId: string,
    draft: string,
    id: string,
    messages: arrayOf(
      shape({
        content: string,
        displayContent: string,
        id: string,
        isPlannerPrompt: bool,
        listings: arrayOf(object),
        plannerPreferences: object,
        role: string,
        suggestedFilters: object,
      }) 
    ),
  }),
  inlineMaxHeight: number,
  locale: string,
  onSuggestedListingsChange: func,
  promptRequest: shape({
    context: object,
    displayMessage: string,
    id: string,
    prompt: string,
  }),
  resultsMode: oneOf(['compact', 'explorer']),
  sessionPersistKey: string,
  showSuggestedPrompts: bool,
  showTrigger: bool,
  signupGateButtonLabel: string,
  signupGateDescription: string,
  signupSearch: string,
  suggestedPrompts: arrayOf(
    shape({
      id: string,
      label: string,
      prompt: string,
    })
  ),
  variant: oneOf(['floating', 'inline']),
};

export default LandingAIChatWidget;


